import { computed, onScopeDispose, ref } from "vue";
import type { Ref } from "vue";
import { useAiWritingToolStore } from "@stores/aiWritingTool";
import type {
  AiFormat,
  AiGenerator,
  AiMenuSelection,
  AiMode,
  AiTransformPayload,
  StreamHandle,
} from "./types";

export interface UseAiTextInputOptions {
  value: Ref<string>;
  generate?: AiGenerator;
  format?: AiFormat;
}

export interface UseAiTextInput {
  mode: Ref<AiMode>;
  readonly: Ref<boolean>;
  displayedValue: Ref<string>;
  showOriginal: Ref<boolean>;
  onSelect: (payload: AiMenuSelection) => void;
  retry: () => void;
  accept: () => void;
  cancel: () => void;
  toggleOriginal: () => void;
}

export function useAiTextInput(options: UseAiTextInputOptions): UseAiTextInput {
  const { value, format } = options;
  const generate = options.generate ?? useAiWritingToolStore().generate;

  const mode = ref<AiMode>("idle");
  const showOriginal = ref(false);
  const originalValue = ref("");
  const previewValue = ref("");
  const lastSelection = ref<AiTransformPayload | null>(null);
  let handle: StreamHandle | null = null;

  const readonly = computed(() => mode.value !== "idle");

  const displayedValue = computed(() => {
    if (mode.value === "idle") return value.value ?? "";
    if (showOriginal.value) return originalValue.value;
    return previewValue.value;
  });

  const cancelStream = (): void => {
    handle?.cancel();
    handle = null;
  };

  const reset = (): void => {
    cancelStream();
    mode.value = "idle";
    previewValue.value = "";
    originalValue.value = "";
    showOriginal.value = false;
    lastSelection.value = null;
  };

  const run = (payload: AiTransformPayload): void => {
    cancelStream();
    previewValue.value = "";
    showOriginal.value = false;
    mode.value = "generating";
    handle = generate(
      {
        originalText: originalValue.value,
        action: payload.action,
        lang: payload.lang,
        prompt: payload.prompt,
        format,
      },
      (accumulated) => {
        previewValue.value = accumulated;
      },
      () => {
        mode.value = "reviewing";
        handle = null;
      },
    );
  };

  const onSelect = (payload: AiMenuSelection): void => {
    if (payload.action === "generate") return;
    originalValue.value = value.value ?? "";
    lastSelection.value = payload;
    run(payload);
  };

  const retry = (): void => {
    if (lastSelection.value) run(lastSelection.value);
  };

  const accept = (): void => {
    value.value = previewValue.value;
    reset();
  };

  const cancel = (): void => reset();

  const toggleOriginal = (): void => {
    showOriginal.value = !showOriginal.value;
  };

  onScopeDispose(cancelStream);

  return {
    mode,
    readonly,
    displayedValue,
    showOriginal,
    onSelect,
    retry,
    accept,
    cancel,
    toggleOriginal,
  };
}
